import React, { useState } from "react";
import { Send, Paperclip } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import useSeo from "@/hooks/useSeo";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const MOTIVOS = [
  "Contactar a la redacción", "Proponer una historia", "Solicitar entrevista", "Enviar información",
  "Correcciones", "Derecho a respuesta", "Publicidad", "Institucional", "Otro",
];

const empty = { nombre: "", apellido: "", email: "", telefono: "", organizacion: "", asunto: "", motivo: "", mensaje: "", acepta_privacidad: false };

export default function Contacto() {
  const [form, setForm] = useState(empty);
  const [fileName, setFileName] = useState("");
  const [sending, setSending] = useState(false);
  useSeo({ title: "Contacto", description: "Comunicate con la redacción de NEXODUM: proponé una historia, solicitá una entrevista o ejercé tu derecho a respuesta." });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.acepta_privacidad) { toast.error("Debés aceptar la política de privacidad."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Mensaje enviado.");
      setForm(empty); setFileName("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo enviar el mensaje.");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="contacto-page">
      <section className="bg-nx-dark text-white">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <h1 className="font-serif font-black text-4xl md:text-6xl tracking-tight">Contacto</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">Escribinos para proponer una historia, solicitar una entrevista, enviar información o ejercer tu derecho a respuesta.</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div><Label htmlFor="nombre">Nombre *</Label><Input id="nombre" required value={form.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="contact-nombre" className="mt-1.5" /></div>
            <div><Label htmlFor="apellido">Apellido</Label><Input id="apellido" value={form.apellido} onChange={(e) => set("apellido", e.target.value)} data-testid="contact-apellido" className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div><Label htmlFor="email">Email *</Label><Input id="email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" className="mt-1.5" /></div>
            <div><Label htmlFor="telefono">Teléfono (opcional)</Label><Input id="telefono" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} data-testid="contact-telefono" className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div><Label htmlFor="organizacion">Organización (opcional)</Label><Input id="organizacion" value={form.organizacion} onChange={(e) => set("organizacion", e.target.value)} data-testid="contact-organizacion" className="mt-1.5" /></div>
            <div>
              <Label>Motivo *</Label>
              <Select value={form.motivo} onValueChange={(v) => set("motivo", v)}>
                <SelectTrigger className="mt-1.5" data-testid="contact-motivo"><SelectValue placeholder="Seleccioná un motivo" /></SelectTrigger>
                <SelectContent>{MOTIVOS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label htmlFor="asunto">Asunto *</Label><Input id="asunto" required value={form.asunto} onChange={(e) => set("asunto", e.target.value)} data-testid="contact-asunto" className="mt-1.5" /></div>
          <div><Label htmlFor="mensaje">Mensaje *</Label><Textarea id="mensaje" required rows={6} value={form.mensaje} onChange={(e) => set("mensaje", e.target.value)} data-testid="contact-mensaje" className="mt-1.5" /></div>

          <div>
            <Label>Adjuntar archivo (opcional)</Label>
            <label className="mt-1.5 flex items-center gap-3 border border-dashed border-gray-300 rounded-md px-4 py-3 cursor-pointer hover:border-nx-orange transition-colors">
              <Paperclip size={18} className="text-gray-400" />
              <span className="text-sm text-gray-500">{fileName || "Seleccionar archivo"}</span>
              <input type="file" className="hidden" onChange={(e) => setFileName(e.target.files?.[0]?.name || "")} data-testid="contact-file" />
            </label>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox id="privacy" checked={form.acepta_privacidad} onCheckedChange={(v) => set("acepta_privacidad", !!v)} data-testid="contact-privacy" className="mt-0.5" />
            <Label htmlFor="privacy" className="text-sm text-gray-600 font-normal leading-snug">Acepto la política de privacidad y el tratamiento de mis datos para responder a esta consulta.</Label>
          </div>

          <button type="submit" disabled={sending} data-testid="contact-submit"
            className="inline-flex items-center gap-2 nx-gradient-bg text-white font-bold px-8 py-3 rounded-full text-sm uppercase tracking-wider hover:opacity-90 transition-opacity disabled:opacity-50">
            {sending ? "Enviando…" : <>Enviar mensaje <Send size={16} /></>}
          </button>
        </form>
      </section>
    </div>
  );
}
